<template>
    <div>
        <div class="container">
          <h5 class="mt-4 mb-4">
            {{ 'CONFIGURATION_MANAGEMENT' | translate('nav') }}
          </h5>
          <div class="form-row">
            <label for="import_type" class="col-auto col-lg-1 col-form-label">{{ 'PRODUCT_LINE' | translate }}</label>
            <div class="col-auto">
              <select class="form-control"
                      v-model="selectedConfigType"
                      name="product_line"
                      id="import_type"
                      required>
                <template v-for="productLine in enabledProductLines">
                  <option :value="productLine">{{ productLine | translate('product') }}</option>
                  <option :value="productLine + editSuffix">{{ productLine + editSuffix | translate('product') }}</option>
                  <option :value="productLine + newSuffix">{{ productLine + newSuffix | translate('product') }}</option>
                </template>
              </select>
            </div>
          </div>
          <hr class="mt-4 mb-4">
          <div class="row">
            <div class="col-lg-5">
              <form v-on:submit.prevent="submitForm()">
                <div class="form-group">
                  <label for="import_file" class="mb-4">
                    {{ 'UPLOAD_CONFIGURATION_FILE' | translate }} ({{ selectedConfigType | translate('product') }}):
                  </label>
                  <div class="form-row">
                    <div class="col-lg-8">
                      <div class="custom-file">
                        <input type="file" class="custom-file-input" id="import_file" name="import_file"
                               v-on:change="selectFile" required>
                        <label class="custom-file-label" for="import_file">
                          {{ (file && file.name) ? file.name : 'UPLOAD_FILE' | translate }}
                        </label>
                      </div>
                    </div>
                    <div class="col">
                      <button type="submit" class="btn btn-primary" v-if="!submitting">{{ 'SUBMIT' | translate }}</button>
                      <div class="cm-loader" v-else>
                        <custom-spinner></custom-spinner>
                      </div>

                    </div>
                  </div>
                </div>
              </form>
              <div class="alert alert-success alert-dismissible mt-5" v-if="successMessage">
                {{ successMessage }}
                <button type="button" class="close" @click="dismissAlertSuccess()" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="alert alert-danger alert-dismissible mt-5" v-if="errorMessage" >
                {{ errorMessage }}
                <button type="button" class="close" @click="dismissAlertError()" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </div>
            <div class="col-lg-7">
              <div class="row">
                <div class="col-auto">
                  <label class="mb-4">{{ 'OR_EDIT_DIRECTLY' | translate }}</label>
                </div>
                <div class="col text-right">
                  <button type="button"
                          class="btn btn-sm btn-default"
                          v-if="selectedConfigType"
                          v-on:click="getTemplateDownloadLink()"
                  >{{ 'DOWNLOAD' | translate }}</button>
                  <button type="button" class="btn btn-sm btn-default"
                          v-on:click="resetFormConfiguration()"
                          :disabled="!cmLoaded">{{ 'LOAD_DEFAULT' | translate }}</button>
                  <button type="button" class="btn btn-sm btn-success"
                          v-on:click="saveConfig(cmContent)"
                          :title="(cmLintErrors.length > 0) ? 'FIX_EDITOR_ERRORS' : '' | translate('msg')"
                          :disabled="!cmLoaded || (cmLintErrors.length > 0)">{{ 'SAVE_CHANGES' | translate }}</button>
                </div>
              </div>
              <div class="cm-container">
                <codemirror v-model="cmContent" :options="cmOptions"></codemirror>
                <div class="cm-loader" v-if="!cmLoaded">
                  <custom-spinner></custom-spinner>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
    import RestService from '../../service/RestService.js';
    import GeneralConfigService from '../../service/GeneralConfigService';
    import FormConfigService from '../../service/FormConfigService.js';
    import { codemirror } from 'vue-codemirror';
    import 'codemirror/lib/codemirror.css';
    import 'codemirror/addon/lint/lint.css';
    import 'codemirror/addon/lint/lint.js';
    import 'codemirror/addon/selection/active-line.js'; // additional option - active line
    // here the codemirror js is included to access the linting callbacks explicitly to get linting errors
    import CodeMirror from 'codemirror/lib/codemirror.js';
    /* yaml-specific */
    import yaml from 'js-yaml';
    import 'codemirror/mode/yaml/yaml.js'; // language highlighting - yaml
    import 'codemirror/addon/lint/yaml-lint.js'; // yaml lint
    /* json-specific */
    import jsonlint from 'jsonlint-mod';
    import 'codemirror/mode/javascript/javascript.js'; // language highlighting - js
    import 'codemirror/addon/lint/json-lint.js'; // json lint
    import CustomSpinner from '../CustomSpinner.vue';

    export default {
      components: {
        codemirror,
        CustomSpinner
      },
      data: function () {
        return {
          rest: this.initRestService(),
          selectedConfigType: '',
          enabledProductLines: [],
          submitting: false,
          file: null,
          selectedConfig: null,
          newSuffix: FormConfigService.typeNewPolicySuffix,
          editSuffix: FormConfigService.typeEditPolicySuffix,
          cmContent: '',
          cmLintErrors: [],
          cmOptions: {
            line: true,
            lineNumbers: true,
            mode: 'text/yaml',
            styleActiveLine: true,
            tabSize: 2,
            viewportMargin: Infinity,
            gutters: ['CodeMirror-lint-markers'],
            lint: {
              getAnnotations: CodeMirror.lint.yaml,
              onUpdateLinting: (lintErrors) => {
                this.cmLintErrors = lintErrors;
              }
            }
          },
          cmLoaded: true,
          successMessage: null,
          errorMessage: null
        }
      },
      async beforeMount() {
        window.jsyaml = yaml;
        window.jsonlint = jsonlint;
        await this.init();
      },
      methods: {
        initRestService() {
          return new RestService();
        },
        async init() {
          this.enabledProductLines = await GeneralConfigService.getEnabledProductLines();
          this.selectedConfigType = this.enabledProductLines.sort()[0];
        },
        submitForm: function () {
          this.submitting = true;
          var reader = new FileReader();
          var vm = this;
          reader.onload = function(e) {
            var fileContent = reader.result;
            vm.saveConfig(fileContent);
            vm.file = null;
            vm.$refs.inputFile.value = '';
          }
          reader.onerror = function(e) {
            vm.$log.error('Failed reading file: ' + e.message);
          }
          reader.readAsText(this.file);
        },
        saveConfig: function(config) {
          this.cmLoaded = false;
          var vm = this;
          this.rest.uploadConfigFile(this.selectedConfigType, config)
            .then((response) => {
              FormConfigService.updateFormConfig(this.selectedConfigType, config);
              vm.selectedConfig = config;
              vm.cmContent = config;
              vm.submitting = false;
              setTimeout(() => {
                vm.cmLoaded = true;
                vm.setSuccessMessage(this.$options.filters.translate('CONFIGURATION_SAVED', 'msg'));
              }, 300);
            }).catch((err) => {
              let finalMessage = '';
              // 422 Unprocessable Entity if there are any validation errors
              if (err.response.status === 422) {
                let errors = err.response.data;
                errors.forEach((error) => { finalMessage += ('\n' + error) });
              } else if (typeof err.response !== 'undefined') {
                this.$log.error(err.response.data);
              } else {
                this.$log.error(err);
              }
              vm.submitting = false;
              vm.cmLoaded = true;
              vm.setErrorMessage(this.$options.filters.translate('ERROR_SAVING_CONFIGURATION', 'msg') + '\n' + finalMessage);
            });
        },
        selectFile: function (evt) {
          this.file = evt.target.files[0];
        },
        getTemplateDownloadLink: function() {
          const url = window.URL.createObjectURL(new Blob([this.selectedConfig]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${this.selectedConfigType}.yaml`);
          link.click();
          window.URL.revokeObjectURL(url);
        },
        loadFormConfiguration: function() {
          this.cmLoaded = false;
          setTimeout(async () => { // giving the browser chance to render the loader before the page freezes
            this.selectedConfig = await FormConfigService.getFormConfig(this.selectedConfigType);
            this.cmContent = this.selectedConfig || ''; // the page freezes here due to rendering 1000+ rows in the editor
            this.cmLoaded = true;
          }, 100);
        },
        resetFormConfiguration: function() {
          this.cmLoaded = false;
          let configType = this.selectedConfigType;
          this.rest.getDefaultConfigForm(configType)
            .then((response) => {
              let config = (configType.endsWith(this.newSuffix) || configType.endsWith(this.editSuffix))
                  ? JSON.stringify(response.data, null, '\t') : response.data;
              this.selectedConfig = config;
              this.cmContent = config;
              setTimeout(() => {
                this.cmLoaded = true;
                this.setSuccessMessage(this.$options.filters.translate('LOADED_DEFAULT_CONFIGURATION', 'msg') +
                ' (' + this.$options.filters.translate(this.selectedConfigType, 'product') + ')');
              }, 300);
            }).catch((err) => {
              if (typeof err.response !== 'undefined') {
                this.$log.error(err.response.data);
              } else {
                this.$log.error(err);
              }
              this.cmLoaded = true;
              this.setErrorMessage(this.$options.filters.translate('ERROR_LOADING_DEFAULT_CONFIGURATION', 'msg'));
            });
        },
        setSuccessMessage: function(message) {
          this.errorMessage = null;
          this.successMessage = message;
          const vm = this;
          setTimeout(function() {
            vm.dismissAlertSuccess();
          }, 3000);
        },
        setErrorMessage: function(message) {
          this.successMessage = null;
          this.errorMessage = message;
        },
        dismissAlertSuccess: function() {
          this.successMessage = null;
        },
        dismissAlertError: function() {
          this.errorMessage = null;
        }
      },
      watch: {
        selectedConfigType(newVal, oldVal) {
          this.cmLoaded = false;
          this.loadFormConfiguration();
          if ((newVal.endsWith(this.newSuffix) || newVal.endsWith(this.editSuffix)) &&
              (!oldVal.endsWith(this.newSuffix) && !oldVal.endsWith(this.editSuffix))) {
            this.cmOptions.mode = 'application/json';
            this.cmOptions.lint.getAnnotations = CodeMirror.lint.json;
          } else if ((!newVal.endsWith(this.newSuffix) && !newVal.endsWith(this.editSuffix)) &&
                     (oldVal.endsWith(this.newSuffix) || oldVal.endsWith(this.editSuffix))) {
            this.cmOptions.mode = 'text/yaml';
            this.cmOptions.lint.getAnnotations = CodeMirror.lint.yaml;
          }
        }
      }
    }
</script>

<style>
  .CodeMirror {
    height: calc(100vh - 300px) !important;
    border: 1px solid #eee;
  }
  .cm-container {
    position: relative;
    overflow: hidden;
  }
</style>



// WEBPACK FOOTER //
// src/components/admin/PolicycaseFormConfigurationManagement.vue